<template>
    <div class="mechanic-history-view">
        <div v-if="showFilters" class="mechanic-history-toolbar">
            <b-form-radio-group
                    v-model="activeCategory"
                    :options="availableFilters"
                    buttons
                    button-variant="outline-secondary"
                    size="sm"
                    class="mechanic-history-filters"
            ></b-form-radio-group>
            <small>{{ filteredEvents.length }} из {{ events.length }}</small>
        </div>

        <b-alert v-if="!filteredEvents.length" show variant="light" class="mb-0">
            {{ events.length ? 'В выбранной категории событий нет' : 'История пуста' }}
        </b-alert>

        <div v-else class="mechanic-event-feed">
            <template v-for="(event, index) in filteredEvents">
                <div
                        v-if="shouldShowDateHeading(event, index)"
                        :key="`heading-${event.id}`"
                        class="mechanic-event-feed__date"
                >
                    {{ dateGroupLabel(event.createdAt) }}
                </div>
                <article
                        :key="`event-${event.id}`"
                        class="mechanic-event"
                        :class="`mechanic-event--${event.variant}`"
                >
                    <div class="mechanic-event__rail" aria-hidden="true">
                        <span><i :class="event.icon"></i></span>
                    </div>
                    <div class="mechanic-event__content">
                        <header class="mechanic-event__header">
                            <div>
                                <strong>{{ event.title }}</strong>
                                <b-badge :variant="event.variant">{{ event.categoryLabel }}</b-badge>
                            </div>
                            <time>{{ formatTime(event.createdAt) }}</time>
                        </header>

                        <div v-if="event.route" class="mechanic-event-route">
                            <div>
                                <small>Откуда</small>
                                <strong>{{ event.route.from || 'Не указано' }}</strong>
                            </div>
                            <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
                            <div>
                                <small>Куда</small>
                                <strong>{{ event.route.to || 'Не указано' }}</strong>
                            </div>
                        </div>
                        <p v-else-if="event.summary" class="mechanic-event__summary">
                            {{ event.summary }}
                        </p>

                        <div class="mechanic-event__meta">
                            <span>
                                <i class="fas fa-user" aria-hidden="true"></i>
                                <EmployeeLink
                                        v-if="event.actorId"
                                        :user="event.actor"
                                        :user-id="event.actorId"
                                        :fallback="event.actorName"
                                        name-order="last-first"
                                />
                                <span v-else>{{ event.actorName }}</span>
                            </span>
                            <router-link
                                    v-if="event.requestId"
                                    :to="`/requests/${event.requestId}`"
                                    :title="event.requestTitle || 'Открыть заявку'"
                            >
                                <i class="fas fa-file-alt" aria-hidden="true"></i>
                                {{ event.requestNumber || `Заявка #${event.requestId}` }}
                            </router-link>
                        </div>

                        <blockquote v-if="event.comment" class="mechanic-event__comment">
                            {{ event.comment }}
                        </blockquote>

                        <button
                                v-if="event.details.length"
                                type="button"
                                class="mechanic-event__details-toggle"
                                :aria-expanded="isExpanded(event.id) ? 'true' : 'false'"
                                @click="toggleExpanded(event.id)"
                        >
                            <i :class="isExpanded(event.id) ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
                            {{ isExpanded(event.id) ? 'Скрыть детали' : detailsButtonText(event.details.length) }}
                        </button>

                        <dl v-if="event.details.length && isExpanded(event.id)" class="mechanic-event-details">
                            <div v-for="(detail, detailIndex) in event.details" :key="`${event.id}-${detailIndex}`">
                                <dt>{{ detail.label }}</dt>
                                <dd v-if="detail.before || detail.after" class="mechanic-event-details__change">
                                    <span>
                                        <small>Было</small>
                                        {{ detail.before || 'Не указано' }}
                                    </span>
                                    <i class="fas fa-long-arrow-alt-right" aria-hidden="true"></i>
                                    <span>
                                        <small>Стало</small>
                                        {{ detail.after || 'Не указано' }}
                                    </span>
                                </dd>
                                <dd v-else>{{ detail.value || 'Не указано' }}</dd>
                            </div>
                        </dl>
                    </div>
                </article>
            </template>
        </div>
    </div>
</template>

<script>
import EmployeeLink from '@/components/EmployeeLink.vue';
import {MECHANIC_HISTORY_FILTERS} from '@/utils/mechanicAssetHistory';

export default {
    name: `MechanicAssetHistoryFeed`,
    components: {
        EmployeeLink
    },
    props: {
        events: {
            type: Array,
            default: () => []
        }
    },
    data() {
        return {
            activeCategory: `all`,
            expandedEvents: {}
        };
    },
    computed: {
        availableFilters() {
            const categories = new Set(this.events.map(event => event.category));
            return MECHANIC_HISTORY_FILTERS.filter(option => option.value === `all` || categories.has(option.value));
        },
        filteredEvents() {
            if (this.activeCategory === `all`) {
                return this.events;
            }
            return this.events.filter(event => event.category === this.activeCategory);
        },
        showFilters() {
            return this.events.length >= 8 && this.availableFilters.length > 2;
        }
    },
    watch: {
        events() {
            if (!this.showFilters || !this.availableFilters.some(option => option.value === this.activeCategory)) {
                this.activeCategory = `all`;
            }
        }
    },
    methods: {
        toggleExpanded(eventId) {
            this.$set(this.expandedEvents, eventId, !this.isExpanded(eventId));
        },
        isExpanded(eventId) {
            return this.expandedEvents[eventId] === true;
        },
        detailsButtonText(count) {
            return count === 1 ? `Показать изменение` : `Показать изменения: ${count}`;
        },
        dateKey(value) {
            const date = new Date(value || 0);
            if (Number.isNaN(date.getTime())) {
                return String(value || ``);
            }
            return [date.getFullYear(), date.getMonth() + 1, date.getDate()].join(`-`);
        },
        shouldShowDateHeading(event, index) {
            if (index === 0) {
                return true;
            }
            return this.dateKey(event.createdAt) !== this.dateKey(this.filteredEvents[index - 1].createdAt);
        },
        dateGroupLabel(value) {
            const date = new Date(value || 0);
            if (Number.isNaN(date.getTime())) {
                return value || `Дата не указана`;
            }
            const today = new Date();
            const todayKey = this.dateKey(today);
            const dateKey = this.dateKey(date);
            if (dateKey === todayKey) {
                return `Сегодня`;
            }
            const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1);
            if (dateKey === this.dateKey(yesterday)) {
                return `Вчера`;
            }
            return date.toLocaleDateString(`ru-RU`, {day: `numeric`, month: `long`, year: `numeric`});
        },
        formatTime(value) {
            const date = new Date(value || 0);
            return Number.isNaN(date.getTime())
                ? `-`
                : date.toLocaleTimeString(`ru-RU`, {hour: `2-digit`, minute: `2-digit`});
        }
    }
};
</script>

<style scoped>
.mechanic-history-view {
    min-width: 0;
}

.mechanic-history-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
}

.mechanic-history-toolbar > small {
    color: #6c757d;
}
.mechanic-event--info .mechanic-event__rail span {
    background: #167484;
}

.mechanic-event--success .mechanic-event__rail span {
    background: #2d7d46;
}

.mechanic-event--warning .mechanic-event__rail span {
    background: #a86400;
}

.mechanic-event--secondary .mechanic-event__rail span {
    background: #66717d;
}

.mechanic-event--dark .mechanic-event__rail span {
    background: #343a40;
}

.mechanic-history-filters {
    display: flex;
    max-width: 100%;
    overflow-x: auto;
}

.mechanic-event-feed {
    min-width: 0;
}

.mechanic-event-feed__date {
    margin: 14px 0 8px 42px;
    color: #495057;
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
}

.mechanic-event {
    display: grid;
    grid-template-columns: 34px minmax(0, 1fr);
    min-width: 0;
    transition: background-color 0.2s ease;
}

.mechanic-event__rail {
    position: relative;
    display: flex;
    justify-content: center;
}

.mechanic-event__rail::after {
    position: absolute;
    top: 31px;
    bottom: 0;
    width: 2px;
    background: #dfe5ec;
    content: "";
}

.mechanic-event:last-child .mechanic-event__rail::after {
    display: none;
}

.mechanic-event__rail span {
    position: relative;
    z-index: 1;
    display: inline-flex;
    width: 26px;
    height: 26px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #fff;
    font-size: 11px;
}

.mechanic-event__content {
    min-width: 0;
    padding: 1px 0 16px 8px;
}

.mechanic-event__header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
}

.mechanic-event__header > div {
    display: flex;
    min-width: 0;
    align-items: center;
    gap: 7px;
    flex-wrap: wrap;
}

.mechanic-event__header strong {
    line-height: 1.25;
}

.mechanic-event__header time {
    flex: 0 0 auto;
    color: #6c757d;
    font-size: 12px;
}

.mechanic-event__summary {
    margin: 5px 0 0;
    color: #343a40;
}

.mechanic-event-route {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 20px minmax(0, 1fr);
    gap: 8px;
    align-items: center;
    max-width: 720px;
    margin-top: 7px;
    padding: 7px 9px;
    border-left: 3px solid #167484;
    background: #f1f7f8;
}

.mechanic-event-route > i {
    color: #167484;
    text-align: center;
}

.mechanic-event-route small,
.mechanic-event-route strong {
    display: block;
}

.mechanic-event-route small {
    color: #6c757d;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
}

.mechanic-event-route strong {
    overflow-wrap: anywhere;
    line-height: 1.25;
}

.mechanic-event__meta {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
    margin-top: 7px;
    color: #6c757d;
    font-size: 12px;
}

.mechanic-event__meta > span,
.mechanic-event__meta > a {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.mechanic-event__comment {
    margin: 8px 0 0;
    padding: 6px 9px;
    border-left: 3px solid #adb5bd;
    background: #f8f9fa;
    color: #495057;
    font-size: 12px;
}

.mechanic-event__details-toggle {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 7px;
    padding: 0;
    border: 0;
    background: transparent;
    color: #0056b3;
    font-size: 12px;
    cursor: pointer;
}

.mechanic-event-details {
    max-width: 760px;
    margin: 8px 0 0;
    border-top: 1px solid #e5e9ed;
}

.mechanic-event-details > div {
    display: grid;
    grid-template-columns: minmax(120px, 190px) minmax(0, 1fr);
    gap: 12px;
    padding: 7px 0;
    border-bottom: 1px solid #e5e9ed;
}

.mechanic-event-details dt,
.mechanic-event-details dd {
    min-width: 0;
    margin: 0;
    overflow-wrap: anywhere;
}

.mechanic-event-details dt {
    color: #6c757d;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
}

.mechanic-event-details__change {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr);
    gap: 7px;
    align-items: center;
}

.mechanic-event-details__change > i {
    color: #6c757d;
    text-align: center;
}

.mechanic-event-details__change small {
    display: block;
    color: #6c757d;
    font-size: 10px;
    text-transform: uppercase;
}

@media (max-width: 767px) {
    .mechanic-history-toolbar {
        align-items: flex-start;
        flex-direction: column;
    }

    .mechanic-history-toolbar > small {
        align-self: flex-end;
    }

    .mechanic-event-feed__date {
        margin-left: 34px;
    }

    .mechanic-event {
        grid-template-columns: 28px minmax(0, 1fr);
    }

    .mechanic-event__content {
        padding-left: 6px;
    }

    .mechanic-event__header {
        align-items: flex-start;
        flex-direction: column;
        gap: 3px;
    }

    .mechanic-event-route {
        grid-template-columns: minmax(0, 1fr);
    }

    .mechanic-event-route > i {
        transform: rotate(90deg);
    }

    .mechanic-event-details > div {
        grid-template-columns: minmax(0, 1fr);
        gap: 3px;
    }

    .mechanic-event-details__change {
        grid-template-columns: minmax(0, 1fr) 14px minmax(0, 1fr);
    }
}
</style>
